/**
 * Synthesized music (Web Audio, no file), in the chiptune way: a bass, an arpeggio, a pad, a lead and drums, on a
 * loop of four bars. One song per mood: the menus, the battle, the volcano (Île de Braise), the Moon. The notes are
 * scheduled a little ahead of the audio clock. Display side only; its volume is an option (sound.ts).
 */
import { sound } from './sound';

export type Mood = 'menu' | 'battle' | 'volcano' | 'space' | 'none';

interface Song {
  readonly bpm: number;
  /** One chord per bar: root (MIDI) and intervals. */
  readonly chords: readonly (readonly [number, readonly number[]])[];
  /** Lead: 16 steps per bar, MIDI notes (0 = rest); played every other loop. */
  readonly lead: readonly number[];
  readonly leadWave: OscillatorType;
  readonly arpWave: OscillatorType;
  /** Drums, 16 steps a bar ('x' = a hit). */
  readonly kick: string;
  readonly snare: string;
  readonly hat: string;
  /** Steps between two bass notes; `bounce`: every other one an octave up. */
  readonly bassEvery: number;
  readonly bounce: boolean;
}

const SONGS: Readonly<Record<Exclude<Mood, 'none'>, Song>> = {
  menu: {
    bpm: 84,
    chords: [[48, [0, 4, 7, 11]], [57, [0, 3, 7, 10]], [53, [0, 4, 7, 11]], [55, [0, 4, 7, 10]]],
    lead: [
      76, 0, 0, 0, 79, 0, 0, 0, 76, 0, 74, 0, 72, 0, 0, 0,
      72, 0, 0, 0, 76, 0, 0, 0, 74, 0, 72, 0, 69, 0, 0, 0,
      69, 0, 72, 0, 77, 0, 0, 0, 76, 0, 74, 0, 72, 0, 0, 0,
      71, 0, 0, 0, 74, 0, 0, 0, 79, 0, 0, 0, 0, 0, 0, 0,
    ],
    leadWave: 'triangle', arpWave: 'triangle',
    kick: 'x.......x.......', snare: '................', hat: '....x.......x...',
    bassEvery: 4, bounce: false,
  },
  battle: {
    bpm: 112,
    chords: [[57, [0, 3, 7]], [53, [0, 4, 7]], [48, [0, 4, 7]], [55, [0, 4, 7]]],
    lead: [
      69, 0, 72, 0, 76, 0, 74, 72, 71, 0, 69, 0, 0, 0, 64, 0,
      65, 0, 69, 0, 72, 0, 71, 69, 67, 0, 65, 0, 0, 0, 0, 0,
      67, 0, 72, 0, 76, 0, 79, 0, 77, 76, 74, 0, 72, 0, 0, 0,
      71, 0, 74, 0, 79, 0, 77, 76, 74, 0, 71, 0, 67, 0, 0, 0,
    ],
    leadWave: 'square', arpWave: 'square',
    kick: 'x...x...x...x...', snare: '....x.......x...', hat: '..x...x...x...x.',
    bassEvery: 2, bounce: true,
  },
  volcano: {
    bpm: 124,
    chords: [[50, [0, 3, 7]], [46, [0, 4, 7]], [48, [0, 4, 7]], [45, [0, 4, 7]]],
    lead: [
      74, 0, 74, 77, 0, 74, 72, 0, 74, 0, 0, 0, 69, 0, 72, 0,
      70, 0, 70, 74, 0, 70, 69, 0, 70, 0, 0, 0, 65, 0, 69, 0,
      72, 0, 72, 76, 0, 72, 70, 0, 72, 0, 0, 0, 67, 0, 72, 0,
      73, 0, 76, 0, 81, 0, 79, 77, 76, 0, 73, 0, 69, 0, 0, 0,
    ],
    leadWave: 'sawtooth', arpWave: 'square',
    kick: 'x.x...x.x.x...x.', snare: '....x.......x...', hat: 'x.x.x.x.x.x.x.x.',
    bassEvery: 2, bounce: true,
  },
  space: {
    bpm: 66,
    chords: [[52, [0, 3, 7, 14]], [48, [0, 4, 7, 11]], [57, [0, 3, 7, 10]], [59, [0, 5, 7]]],
    lead: [
      79, 0, 0, 0, 0, 0, 0, 0, 78, 0, 0, 0, 76, 0, 0, 0,
      76, 0, 0, 0, 0, 0, 0, 0, 71, 0, 0, 0, 0, 0, 0, 0,
      72, 0, 0, 0, 76, 0, 0, 0, 79, 0, 0, 0, 0, 0, 0, 0,
      78, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    ],
    leadWave: 'triangle', arpWave: 'triangle',
    kick: '................', snare: '................', hat: '................',
    bassEvery: 8, bounce: false,
  },
};

const freq = (n: number) => 440 * Math.pow(2, (n - 69) / 12);

class Music {
  private mood: Mood = 'none';
  private song: Song | null = null;
  private step = 0;
  private next = 0;
  private timer = 0;
  /** This song's own gain, to fade it in and out. */
  private out: GainNode | null = null;
  /** One second of white noise for the drums, made once. */
  private noiseBuf: AudioBuffer | null = null;

  /** For scripted tests: the song playing and the steps scheduled so far. */
  debug(): { mood: Mood; step: number } {
    return { mood: this.mood, step: this.step };
  }

  /** Plays the song of `mood` (from its start, after a short fade), or nothing ('none'). */
  play(mood: Mood): void {
    if (mood === this.mood) return;
    this.mood = mood;
    const a = sound.audio();
    if (a && this.out) {
      const old = this.out;
      old.gain.setTargetAtTime(0, a.ctx.currentTime, 0.25);
      window.setTimeout(() => old.disconnect(), 2000);
    }
    this.out = null;
    this.song = mood === 'none' ? null : SONGS[mood];
    this.step = 0;
    if (!this.timer) this.timer = window.setInterval(() => this.pump(), 40);
  }

  private pump(): void {
    const a = sound.audio();
    if (!a || !this.song || document.hidden) return;
    const { ctx } = a;
    if (!this.out) {
      this.out = ctx.createGain();
      this.out.gain.value = 0;
      this.out.connect(a.bus);
      this.out.gain.setTargetAtTime(1, ctx.currentTime, 0.6);
      this.next = ctx.currentTime + 0.1;
    }
    // Back from a hidden tab: start again from now rather than catch up.
    if (this.next < ctx.currentTime - 0.3) this.next = ctx.currentTime + 0.05;
    const dur = 60 / this.song.bpm / 4;
    while (this.next < ctx.currentTime + 0.2) {
      this.playStep(ctx, this.out, this.song, this.step, this.next, dur);
      this.next += dur;
      this.step++;
    }
  }

  private playStep(ctx: AudioContext, out: GainNode, song: Song, step: number, t: number, dur: number): void {
    const bars = song.chords.length;
    const bar = Math.floor(step / 16) % bars;
    const s = step % 16;
    const loop = Math.floor(step / (16 * bars));
    const [root0, intervals] = song.chords[bar];
    let root = root0;
    while (root < 48) root += 12;
    while (root > 59) root -= 12;
    const chord = intervals.map((i) => root + i);
    if (s % song.bassEvery === 0) {
      const up = song.bounce && (s / song.bassEvery) % 2 === 1 ? 12 : 0;
      this.note(ctx, out, 'triangle', root - 12 + up, t, dur * song.bassEvery * 0.9, 0.2);
    }
    this.note(ctx, out, song.arpWave, chord[s % chord.length] + 12, t, dur * 0.85, song.arpWave === 'square' ? 0.03 : 0.045);
    if (s === 0) for (const n of chord) this.pad(ctx, out, n, t, dur * 16);
    const lead = song.lead[bar * 16 + s];
    if (lead > 0 && loop % 2 === 1) this.note(ctx, out, song.leadWave, lead, t, dur * 1.8, song.leadWave === 'sawtooth' ? 0.04 : 0.06);
    if (song.kick[s] === 'x') this.kick(ctx, out, t);
    if (song.snare[s] === 'x') this.noise(ctx, out, t, 'bandpass', 1800, 0.12, 0.14);
    if (song.hat[s] === 'x') this.noise(ctx, out, t, 'highpass', 7000, 0.03, 0.05);
  }

  private note(ctx: AudioContext, out: GainNode, wave: OscillatorType, n: number, t: number, len: number, gain: number): void {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = wave;
    o.frequency.value = freq(n);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + len);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + len + 0.02);
  }

  /** A held chord note: a soft sawtooth through a low-pass filter, slow attack and release. */
  private pad(ctx: AudioContext, out: GainNode, n: number, t: number, len: number): void {
    const o = ctx.createOscillator();
    const f = ctx.createBiquadFilter();
    const g = ctx.createGain();
    o.type = 'sawtooth';
    o.frequency.value = freq(n);
    f.type = 'lowpass';
    f.frequency.value = 1100;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.02, t + len * 0.25);
    g.gain.exponentialRampToValueAtTime(0.0001, t + len);
    o.connect(f).connect(g).connect(out);
    o.start(t);
    o.stop(t + len + 0.05);
  }

  private kick(ctx: AudioContext, out: GainNode, t: number): void {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(130, t);
    o.frequency.exponentialRampToValueAtTime(40, t + 0.14);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.35, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + 0.18);
  }

  private noise(ctx: AudioContext, out: GainNode, t: number, type: BiquadFilterType, hz: number, len: number, gain: number): void {
    if (!this.noiseBuf) {
      this.noiseBuf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const d = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = hz;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + len);
    src.connect(f).connect(g).connect(out);
    src.start(t, Math.random() * 0.8, len);
  }
}

export const music = new Music();

/** The mood of a map: the volcano and the Moon have their own songs. */
export function moodOf(mapId: string): Mood {
  return mapId === 'ember' ? 'volcano' : mapId === 'moon' ? 'space' : 'battle';
}
